import { BACKEND_API_URL } from '../consts/api'

const PROKA_WEB_PUSH_ENDPOINT_KEY = 'proka_web_push_endpoint'

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function getPushPermission(): NotificationPermission | 'unsupported' {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }
  return Notification.permission
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function storeEndpoint(endpoint: string | null): void {
  if (endpoint) {
    localStorage.setItem(PROKA_WEB_PUSH_ENDPOINT_KEY, endpoint)
  } else {
    localStorage.removeItem(PROKA_WEB_PUSH_ENDPOINT_KEY)
  }
}

function getStoredEndpoint(): string | null {
  return localStorage.getItem(PROKA_WEB_PUSH_ENDPOINT_KEY)
}

export async function subscribeToWebPush(vapidPublicKey: string): Promise<PushSubscriptionJSON> {
  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  if (existing) {
    const json = existing.toJSON()
    if (json.endpoint) {
      storeEndpoint(json.endpoint)
    }
    return json
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  })

  const json = subscription.toJSON()
  if (json.endpoint) {
    storeEndpoint(json.endpoint)
  }
  return json
}

export async function unsubscribeFromWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    storeEndpoint(null)
    return
  }

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (subscription) {
    await subscription.unsubscribe()
  }
  storeEndpoint(null)
}

export async function cleanupWebPushSubscription(): Promise<void> {
  let endpoint = getStoredEndpoint()

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        endpoint = subscription.endpoint
        await subscription.unsubscribe()
      }
    }
  } catch {
    // best effort
  }

  if (endpoint) {
    try {
      await fetch(`${BACKEND_API_URL}notifications/push-subscriptions`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint }),
        keepalive: true,
      })
    } catch {
      // best effort
    }
  }

  storeEndpoint(null)
}
